import React, { memo, useState, Fragment, useEffect, useRef, useContext, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { gql, useQuery, useMutation } from '@apollo/client'
import { GuideContext } from '../../context/guide-context'
import { LANDING_PAGE_FIELDS } from '../../graphql/landing-page-fragment'
import Card from '../../components/common/Card'
import LandingPageForm from '../../components/campaigns/LandingPageForm'
import { showNotification } from '../../utils/notifications'
import { useOnboardingTour } from '../../utils/onboarding-tour-hook'
import { ToastContainer } from 'react-toastify'
import { CubeLoader } from '@/components/ui/cube-loader'

const CREATE_LANDING_PAGE = gql`
    ${LANDING_PAGE_FIELDS}
    mutation CreateLandingPage($campaignId: ID!, $landingPageInput: LandingPageInput) {
        createLandingPage(campaignId: $campaignId, landingPageInput: $landingPageInput) {
            ...LandingPageFields
        }
    }
`

const UPDATE_LANDING_PAGE = gql`
    ${LANDING_PAGE_FIELDS}
    mutation UpdateLandingPage($landingPageId: ID!, $landingPageInput: LandingPageInput) {
        updateLandingPage(landingPageId: $landingPageId, landingPageInput: $landingPageInput) {
            ...LandingPageFields
        }
    }
`

const DELETE_LANDING_PAGE = gql`
    mutation deleteDraftLandingPage($landingPageId: ID!) {
        deleteDraftLandingPage(landingPageId: $landingPageId)
    }
`

const GET_THEMES = gql`
    query Themes($options: ThemeOptions) {
        themes(options: $options) {
            id
            name
        }
    }
`

const LandingPage = memo(() => {
    const { guide } = useContext(GuideContext)
    const params = useParams()
    const campaignId = params.id
    const [updatedData, setUpdatedData] = useState({})
    const [errors, setError] = useState([])
    const navigate = useNavigate()
    const { focus } = useOnboardingTour()
    const guidedRef = useRef(false)
    const autosaveTimeoutRef = useRef(null)
    const [draftId, setDraftId] = useState(null)
    const [autosaveStatus, setAutosaveStatus] = useState(null)
    const [isAutosaveEnabled, setIsAutosaveEnabled] = useState(false)
    // Store the time of the last save
    const [lastSaveTime, setLastSaveTime] = useState(null)

    // Format the timestamp
    const formatTime = (date) => {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    }

    const themesQuery = useQuery(GET_THEMES, {
        variables: {
            options: { sort: [{ order: 'ASC' }] },
        },
    })

    // Create landing page callback - only called from explicit form submission
    const createLandingPageCallback = async () => {
        // Create a deep clone of the updated data
        const safeData = JSON.parse(JSON.stringify(updatedData))

        // Always assign a temporary domain if empty or null
        if (!safeData.domain || safeData.domain.trim() === '') {
            safeData.domain = `draft-${campaignId}-${Date.now()}`
        }

        // Double-check domain one more time
        if (!safeData.domain || safeData.domain === '') {
            setError([{ message: 'Domain cannot be empty. Please enter a domain or try again.' }])
            return
        }

        if (draftId) {
            // If we have a draft, update it instead of creating a new one
            try {
                await updateLandingPage({
                    variables: {
                        landingPageId: draftId,
                        landingPageInput: {
                            ...getLandingPageInput(safeData),
                            domain: safeData.domain, // Double ensure domain is set
                            status: 'ACTIVE', // Change status from DRAFT to ACTIVE
                        },
                    },
                })

                navigate(`/campaigns/${campaignId}/landing-pages/`, {
                    state: {
                        notification: {
                            status: 'success',
                            message: 'Landing Page has been published successfully!',
                        },
                        refetch: true,
                    },
                })
            } catch (error) {
                console.error('Error publishing draft:', error)

                // Check for domain duplication error
                if (
                    error.graphQLErrors &&
                    error.graphQLErrors.some(
                        (err) =>
                            err.message.includes('domain is already used') ||
                            err.extensions?.code === 'DOMAIN_ALREADY_EXISTS',
                    )
                ) {
                    setError([{ message: 'This domain is already in use. Please enter a different domain.' }])
                } else {
                    setError([{ message: 'Error publishing draft' }])
                }
            }
        } else {
            try {
                await createLandingPage({
                    variables: {
                        campaignId,
                        landingPageInput: {
                            ...getLandingPageInput(safeData),
                            domain: safeData.domain, // Double ensure domain is set
                        },
                    },
                })

                navigate(`/campaigns/${campaignId}/landing-pages/`, {
                    state: {
                        notification: {
                            status: 'success',
                            message: 'Landing Page has been created successfully!',
                        },
                        refetch: true,
                    },
                })
            } catch (error) {
                console.error('Error creating landing page:', error)

                // Check for domain duplication error
                if (
                    error.graphQLErrors &&
                    error.graphQLErrors.some(
                        (err) =>
                            err.message.includes('domain is already used') ||
                            err.extensions?.code === 'DOMAIN_ALREADY_EXISTS',
                    )
                ) {
                    setError([{ message: 'This domain is already in use. Please enter a different domain.' }])
                } else {
                    setError([{ message: 'Error creating landing page' }])
                }
            }
        }
    }

    const cancelCallback = async () => {
        // If there's a draft, confirm before discarding
        if (draftId) {
            try {
                await deleteDraftLandingPage({
                    variables: { landingPageId: draftId },
                })

                navigate(`/campaigns/${campaignId}/landing-pages/`, {
                    state: {
                        notification: {
                            status: 'success',
                            message: 'Draft has been discarded.',
                        },
                        refetch: true,
                    },
                })
            } catch (error) {
                console.error('Error deleting draft:', error)

                // Navigate away anyway, even if deletion failed
                navigate(`/campaigns/${campaignId}/landing-pages/`)
            }
        } else {
            // No draft to discard, just navigate away
            navigate(`/campaigns/${campaignId}/landing-pages/`)
        }
    }

    const updateData = (data) => {
        setUpdatedData(data)

        // Enable autosave when title and theme are selected
        if (data.title && data.theme && !isAutosaveEnabled) {
            setIsAutosaveEnabled(true)
        }
    }

    const setFormFieldsFromFormData = (formData) => {
        const formFields = []
        for (const fieldNumber of [1, 2, 3, 4, 5, 6, 7, 8, 9]) {
            const label = formData[`field${fieldNumber}Label`]
            if (label && label.length > 0) {
                formFields.push({
                    label,
                    required: formData[`field${fieldNumber}Required`],
                    type: formData[`field${fieldNumber}Type`],
                    enabled: formData[`field${fieldNumber}Enabled`],
                })
            }
        }
        return JSON.stringify(formFields)
    }

    const setButtonSettingsFromFormData = (formData) => {
        const { buttonLabel, buttonColour } = formData || {}
        return JSON.stringify({ buttonLabel, buttonColour })
    }

    const setPageSettingsFromFormData = (formData) => {
        const {
            pageHeading,
            pageHeadingColor,
            pageHeadingFont,
            pageSubHeading,
            pageSubHeadingColor,
            pageSubHeadingFont,
        } = formData || {}
        return JSON.stringify({
            pageHeading,
            pageHeadingColor,
            pageHeadingFont,
            pageSubHeading,
            pageSubHeadingColor,
            pageSubHeadingFont,
        })
    }

    const setPagePopupSettingsFromFormData = (formData) => {
        const { popupTitle, popupDescription, popupButtonLabel, popupButtonUrl, popupButtonColour } = formData || {}
        return JSON.stringify({ popupTitle, popupDescription, popupButtonLabel, popupButtonUrl, popupButtonColour })
    }

    // Function to get the landing page input object
    const getLandingPageInput = (data) => {
        // Always ensure a domain exists, even if blank, to prevent null domains
        const tempDomain = data.domain && data.domain.trim() !== '' ? data.domain : `draft-${campaignId}-${Date.now()}`

        return {
            themeId: data.theme,
            title: data.title,
            domain: tempDomain,
            email: data.email || '',
            status: draftId ? 'DRAFT' : data.status || 'ACTIVE',
            formFields: setFormFieldsFromFormData(data),
            metaKeywords: data.metaKeywords || '',
            metaDescription: data.metaDescription || '',
            customHtmlHeader: data.customHtmlHeader || '',
            hasForm: data.hasForm === undefined ? true : data.hasForm,
            formButtonSettings: setButtonSettingsFromFormData(data),
            pageSettings: setPageSettingsFromFormData(data),
            pagePopupSettings: setPagePopupSettingsFromFormData(data),
            formContent: data.formContent || '',
            logo: data.logo || '',
            logoImage: data.logoImage !== '' ? data.logoImage : null,
            background: data.background ? data.background : 'image',
            backgroundImage: data.backgroundImage !== '' ? data.backgroundImage : null,
            backgroundVideo: data.backgroundVideo !== '' ? data.backgroundVideo : null,
            termsAndConditions: data.termsAndConditions || '',
        }
    }

    // Handle error for CreateLandingPage mutation
    const [createLandingPage, mutation] = useMutation(CREATE_LANDING_PAGE, {
        onError({ graphQLErrors }) {
            setError(graphQLErrors)

            // Check if any of the errors is about the domain being null
            if (
                graphQLErrors.some(
                    (err) =>
                        err.message.includes('domain') &&
                        (err.message.includes('null') || err.message.includes('required')),
                )
            ) {
                console.error('Detected domain null error - showing notification')

                showNotification(
                    <div>
                        <p>Error: The domain field cannot be empty.</p>
                        <p>Please enter a domain or contact support if this issue persists.</p>
                    </div>,
                    'error',
                    true,
                )
            }
        },
    })

    // Separate autosave mutation for creating drafts
    const [createDraft] = useMutation(CREATE_LANDING_PAGE, {
        onError({ graphQLErrors }) {
            setAutosaveStatus('error')
            console.error('Autosave error (create):', graphQLErrors)

            // Check if any of the errors is about the domain being null
            if (
                graphQLErrors.some(
                    (err) =>
                        err.message.includes('domain') &&
                        (err.message.includes('null') || err.message.includes('required')),
                )
            ) {
                console.error('Detected domain null error during autosave - showing notification')

                showNotification(
                    <div>
                        <p>Error saving draft: The domain field cannot be empty.</p>
                        <p>Temporary domain generation failed. Please enter a domain manually.</p>
                    </div>,
                    'error',
                    true,
                )
                return
            }

            // Show toast notification for duplicate domain error
            if (
                graphQLErrors.some(
                    (error) =>
                        error.message.includes('domain is already used') ||
                        error.extensions?.code === 'DOMAIN_ALREADY_EXISTS',
                )
            ) {
                showNotification(
                    <div>
                        <p>Cannot autosave: The domain you entered is already in use.</p>
                        <p>Please enter a different domain to continue.</p>
                    </div>,
                    'error',
                    true,
                )
            }
        },
        onCompleted: (data) => {
            setLastSaveTime(new Date())
            setAutosaveStatus('saved')
            // Store the draft ID
            if (data?.createLandingPage?.id) {
                setDraftId(data.createLandingPage.id)
            }
        },
    })

    // Update landing page mutation for autosave
    const [updateLandingPage] = useMutation(UPDATE_LANDING_PAGE, {
        onError({ graphQLErrors }) {
            setAutosaveStatus('error')
            console.error('Autosave error (update):', graphQLErrors)

            // Check if any of the errors is about the domain being null
            if (
                graphQLErrors.some(
                    (err) =>
                        err.message.includes('domain') &&
                        (err.message.includes('null') || err.message.includes('required')),
                )
            ) {
                console.error('Detected domain null error during autosave - showing notification')

                showNotification(
                    <div>
                        <p>Error saving draft: The domain field cannot be empty.</p>
                        <p>Temporary domain generation failed. Please enter a domain manually.</p>
                    </div>,
                    'error',
                    true,
                )
                return
            }

            // Show toast notification for duplicate domain error
            if (
                graphQLErrors.some(
                    (error) =>
                        error.message.includes('domain is already used') ||
                        error.extensions?.code === 'DOMAIN_ALREADY_EXISTS',
                )
            ) {
                showNotification(
                    <div>
                        <p>Cannot autosave: The domain you entered is already in use.</p>
                        <p>Please enter a different domain to continue.</p>
                    </div>,
                    'error',
                    true,
                )
            }
        },
        onCompleted: (data) => {
            setLastSaveTime(new Date())
            setAutosaveStatus('saved')
            // If this is the first time saving a draft, store its ID
            if (!draftId && data.updateLandingPage) {
                setDraftId(data.updateLandingPage.id)
            }
        },
    })

    // Delete landing page mutation
    const [deleteDraftLandingPage] = useMutation(DELETE_LANDING_PAGE, {
        onError({ graphQLErrors }) {
            console.error('Error deleting draft:', graphQLErrors)
        },
    })

    // Autosave function
    const performAutosave = useCallback(() => {
        // Only autosave if we have title and theme selected
        if (!updatedData.title || !updatedData.theme) {
            return
        }

        setAutosaveStatus('saving')

        const saveLandingPage = async () => {
            try {
                // Create a deep clone of the updated data
                const safeData = JSON.parse(JSON.stringify(updatedData))

                // Always assign a temporary domain if empty or null
                if (!safeData.domain || safeData.domain.trim() === '') {
                    safeData.domain = `draft-${campaignId}-${Date.now()}`
                }

                // Create input with guaranteed domain
                const inputData = {
                    ...getLandingPageInput(safeData),
                    domain: safeData.domain, // Explicitly set domain again for extra safety
                    status: 'DRAFT', // Ensure it remains a draft
                }

                // Double-check domain one more time
                if (!inputData.domain || inputData.domain === '') {
                    console.error('Domain is still null/empty after safety checks - aborting autosave')
                    setAutosaveStatus('error')
                    return
                }

                if (draftId) {
                    // Update existing draft
                    await updateLandingPage({
                        variables: {
                            landingPageId: draftId,
                            landingPageInput: inputData,
                        },
                    })
                } else {
                    // Create new draft
                    await createDraft({
                        variables: {
                            campaignId: campaignId,
                            landingPageInput: inputData,
                        },
                    })
                }
            } catch (error) {
                console.error('Autosave failed:', error)
                setAutosaveStatus('error')
            }
        }

        saveLandingPage()
    }, [updatedData, draftId, campaignId, updateLandingPage, createDraft])

    // Schedule autosave when data changes
    useEffect(() => {
        if (!isAutosaveEnabled) return

        // Clear any existing timeout
        if (autosaveTimeoutRef.current) {
            clearTimeout(autosaveTimeoutRef.current)
        }

        // Set new timeout for autosave (3 seconds after last update)
        autosaveTimeoutRef.current = setTimeout(() => {
            performAutosave()
        }, 3000)

        // Cleanup
        return () => {
            if (autosaveTimeoutRef.current) {
                clearTimeout(autosaveTimeoutRef.current)
            }
        }
    }, [updatedData, isAutosaveEnabled, performAutosave])

    // Cleanup draft if user navigates away before saving
    useEffect(() => {
        return () => {
            // If we're leaving and there's a draft, but the form wasn't explicitly submitted
            // We don't delete the draft as it might be useful to come back to it
        }
    }, [draftId, deleteDraftLandingPage])

    useEffect(() => {
        if (!guidedRef.current && guide === 'on') {
            showNotification(
                <div>
                    <p>Please enter the details for your creative in the fields below.</p>
                    <p>
                        If you need help, hover your mouse over the question mark icon to read the tooltip information.
                    </p>
                    <p>
                        Your work will be automatically saved as a draft when you fill in the title and select a theme.
                    </p>
                </div>,
                'info',
                false,
                'Continue',
                () => focus('.lp-step-1'),
                false,
            )
            guidedRef.current = true
        }
    }, [guidedRef, focus, guide])

    // Autosave status message component
    const AutosaveStatus = () => {
        if (!isAutosaveEnabled) return null

        let message = ''
        let bgColor = ''

        switch (autosaveStatus) {
            case 'saving':
                message = 'Saving draft...'
                bgColor = 'bg-warning'
                break
            case 'saved':
                message = lastSaveTime ? `Draft saved at ${formatTime(lastSaveTime)}` : 'Draft saved'
                bgColor = 'bg-success'
                break
            case 'error':
                message = 'Error saving draft'
                bgColor = 'bg-danger'
                break
            default:
                return null
        }

        return (
            <div className={`autosave-status ${bgColor} text-white rounded px-2 py-1 my-2`}>
                <small>{message}</small>
            </div>
        )
    }

    return (
        <Fragment>
            <ToastContainer />
            {themesQuery.loading ? (
                <CubeLoader size="default" />
            ) : (
                <Card>
                    <Card.Header className="d-flex justify-content-between">
                        <div className="header-title">
                            <h4 className="card-title">
                                <i className="icon">
                                    <svg
                                        className="icon-25"
                                        width="25"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            opacity="0.4"
                                            d="M19.9926 18.9532H14.2983C13.7427 18.9532 13.2909 19.4123 13.2909 19.9766C13.2909 20.5421 13.7427 21 14.2983 21H19.9926C20.5481 21 21 20.5421 21 19.9766C21 19.4123 20.5481 18.9532 19.9926 18.9532Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            d="M10.309 6.90388L15.7049 11.264C15.835 11.3682 15.8573 11.5596 15.7557 11.6929L9.35874 20.0282C8.95662 20.5431 8.36402 20.8345 7.72908 20.8452L4.23696 20.8882C4.05071 20.8904 3.88775 20.7614 3.84542 20.5765L3.05175 17.1258C2.91419 16.4916 3.05175 15.8358 3.45388 15.3306L9.88256 6.95548C9.98627 6.82111 10.1778 6.79746 10.309 6.90388Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            opacity="0.4"
                                            d="M18.1206 8.66544L17.0804 9.96401C16.9756 10.0962 16.7872 10.1177 16.6571 10.0124C15.3925 8.98901 12.1544 6.36285 11.2559 5.63509C11.1247 5.52759 11.1067 5.33625 11.2125 5.20295L12.2157 3.95706C13.1258 2.78534 14.7131 2.67784 15.9936 3.69906L17.4645 4.87078C18.0677 5.34377 18.4698 5.96726 18.6074 6.62299C18.7661 7.3443 18.5968 8.0527 18.1206 8.66544Z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                </i>{' '}
                                Create Landing Page
                                {draftId && <span className="ms-2 badge bg-warning">Draft</span>}
                            </h4>
                        </div>
                        {isAutosaveEnabled && <AutosaveStatus />}
                    </Card.Header>
                    <Card.Body>
                        <div className="new-user-info">
                            <LandingPageForm
                                updateData={updateData}
                                callback={createLandingPageCallback}
                                cancelCallback={cancelCallback}
                                errors={errors}
                                mutation={mutation}
                                themes={themesQuery}
                                campaignId={campaignId}
                                guidance={guide === 'on'}
                            />
                        </div>
                    </Card.Body>
                </Card>
            )}
        </Fragment>
    )
})

export default LandingPage
